import { RefObject, useCallback, useEffect, useState } from 'react';

// https://usehooks-ts.com/react-hook/use-intersection-observer
interface Args extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

function useIntersectionObservers(
  elementRefs: RefObject<Element[]>,
  { threshold = 0, root = null, rootMargin = '0%', freezeOnceVisible = false }: Args,
  callback: (entries: IntersectionObserverEntry[]) => void,
) {
  const frozen = freezeOnceVisible;
  const [entry, setEntry] = useState<IntersectionObserverEntry[]>();

  const updateEntry = useCallback((entries: IntersectionObserverEntry[]): void => {
    callback(entries);
    setEntry(entries);
  }, []);

  useEffect(() => {
    const nodes = elementRefs?.current?.filter((item) => item); // DOM Ref
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !nodes?.length) return;
    const observerParams = { threshold, root, rootMargin };

    const observer = new IntersectionObserver(updateEntry, observerParams);

    (elementRefs?.current || []).map((node) => {
      if (node) {
        observer.observe(node);
      }
    });

    return () => {
      observer.disconnect();
    };

    // eslint-disable-next-line
  }, [
    JSON.stringify(Object.keys(elementRefs.current || {})),
    JSON.stringify(threshold),
    root,
    rootMargin,
    frozen,
  ]);

  return entry;
}

export default useIntersectionObservers;
